/**
 * StatusPill — Badge tipado pelos status do domínio (jobs, nerds, invoices, etc).
 */
import { Badge } from "@/components/ui/badge";

export type JobStatus =
  | "draft"
  | "open"
  | "dispatched"
  | "assigned"
  | "in_progress"
  | "completed"
  | "approved"
  | "unproductive"
  | "cancelled"
  | "expired";

const JOB_STATUS: Record<JobStatus, { label: string; variant: "default" | "primary" | "success" | "warning" | "danger" | "info" | "purple" | "muted" }> = {
  draft: { label: "Rascunho", variant: "muted" },
  open: { label: "Aberto", variant: "info" },
  dispatched: { label: "Distribuído", variant: "purple" },
  assigned: { label: "Atribuído", variant: "primary" },
  in_progress: { label: "Em atendimento", variant: "warning" },
  completed: { label: "Concluído", variant: "success" },
  approved: { label: "Aprovado", variant: "success" },
  unproductive: { label: "Improdutivo", variant: "danger" },
  cancelled: { label: "Cancelado", variant: "danger" },
  expired: { label: "Expirado", variant: "muted" },
};

export function JobStatusPill({ status }: { status: JobStatus }) {
  const meta = JOB_STATUS[status] ?? { label: status, variant: "default" as const };
  return <Badge variant={meta.variant} dot>{meta.label}</Badge>;
}

export type NerdStatus = "pending" | "approved" | "suspended" | "rejected";

const NERD_STATUS: Record<NerdStatus, { label: string; variant: "primary" | "success" | "warning" | "danger" }> = {
  pending: { label: "Pendente", variant: "warning" },
  approved: { label: "Aprovado", variant: "success" },
  suspended: { label: "Suspenso", variant: "danger" },
  rejected: { label: "Rejeitado", variant: "danger" },
};

export function NerdStatusPill({ status }: { status: NerdStatus }) {
  const meta = NERD_STATUS[status] ?? { label: status, variant: "primary" as const };
  return <Badge variant={meta.variant} dot>{meta.label}</Badge>;
}

export type InvoiceStatus =
  | "awaiting_nf"
  | "reviewing"
  | "scheduled"
  | "pending_fix"
  | "rejected"
  | "paid"
  | "cancelled";

const INVOICE_STATUS: Record<InvoiceStatus, { label: string; variant: "default" | "primary" | "success" | "warning" | "danger" | "info" | "purple" | "muted" }> = {
  awaiting_nf: { label: "Aguardando NF", variant: "warning" },
  reviewing: { label: "Em análise", variant: "info" },
  scheduled: { label: "Programado", variant: "primary" },
  pending_fix: { label: "Pendência", variant: "warning" },
  rejected: { label: "Recusada", variant: "danger" },
  paid: { label: "Paga", variant: "success" },
  cancelled: { label: "Cancelada", variant: "muted" },
};

export function InvoiceStatusPill({ status }: { status: InvoiceStatus }) {
  const meta = INVOICE_STATUS[status] ?? { label: status, variant: "default" as const };
  return <Badge variant={meta.variant} dot>{meta.label}</Badge>;
}

export type UserRole =
  | "super_admin"
  | "admin"
  | "operator"
  | "financial"
  | "client"
  | "nerd"
  | "guest";

const ROLE_META: Record<UserRole, { label: string; variant: "default" | "primary" | "success" | "warning" | "danger" | "info" | "purple" | "muted" }> = {
  super_admin: { label: "Super Admin", variant: "purple" },
  admin: { label: "Admin", variant: "primary" },
  operator: { label: "Operador", variant: "info" },
  financial: { label: "Financeiro", variant: "warning" },
  client: { label: "Cliente", variant: "default" },
  nerd: { label: "Técnico", variant: "success" },
  guest: { label: "Convidado", variant: "muted" },
};

export function RolePill({ role }: { role: UserRole }) {
  const meta = ROLE_META[role] ?? { label: role, variant: "default" as const };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

export type JobUrgency = "normal" | "same_day" | "urgent";

const URGENCY_META: Record<JobUrgency, { label: string; variant: "default" | "warning" | "danger" }> = {
  normal: { label: "Normal", variant: "default" },
  same_day: { label: "Mesmo dia", variant: "warning" },
  urgent: { label: "Urgente", variant: "danger" },
};

export function UrgencyPill({ urgency }: { urgency: JobUrgency }) {
  const meta = URGENCY_META[urgency] ?? { label: urgency, variant: "default" as const };
  return <Badge variant={meta.variant} size="sm">{meta.label}</Badge>;
}

export type ContractType = "bodyshop" | "avulso" | "credito" | "projetos" | "baseline";

const CONTRACT_META: Record<ContractType, { label: string; variant: "info" | "primary" | "warning" | "purple" | "success" }> = {
  bodyshop: { label: "BodyShop", variant: "primary" },
  avulso: { label: "Avulso", variant: "info" },
  credito: { label: "Crédito", variant: "warning" },
  projetos: { label: "Projetos", variant: "purple" },
  baseline: { label: "Baseline", variant: "success" },
};

export function ContractTypePill({ type }: { type: ContractType }) {
  const meta = CONTRACT_META[type] ?? { label: type, variant: "info" as const };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}
